import { memo, useCallback, useEffect, useMemo } from 'react'
import ReactDragListView from 'react-drag-listview/lib/index.js'
import { Col, Row } from 'reactstrap'
import { setQueryData } from '../../api/helpers'
import { handleImagesOrder } from '../../api/images/helpers'
import images from '../../api/images/hooks'
import AppPermissionsSwitcher from '../AppPermissionsSwitcher/AppPermissionsSwitcher'
import Loader from '../Common/Loader'
import { useBlobUrls } from '../Hooks/useBlobUrl'
import ImageCard from './ImageCard'
import { AllowedPhotosProps, ImagesGalleryProps } from './types'

const ImagesGallery = (props: ImagesGalleryProps) => {
    const { isFetching, imageList, imageData, module, queryPayload, handleEditorImage, handleSelectImage, syncImage } = props

    const imageUrlQueries = images.GetImagesByUrl.useQueries(imageData || [], '')
    const blobUrls = useBlobUrls(imageUrlQueries)

    const dragProps = useMemo(
        () => ({
            onDragEnd(fromIndex: number, toIndex: number) {
                const { collection, module, parent } = queryPayload
                const updatedImages = handleImagesOrder(fromIndex, toIndex, imageData)
                
                setQueryData([collection, module, parent.toString()], updatedImages)
            },
            nodeSelector: '.drag-node',
            handleSelector: '.mdi-menu',
            lineClassName: 'dmn-photo-drag',
        }),
        [queryPayload, imageData]
    )

    const handlePhotoSync = (id: number) => {
        syncImage({ id, ...queryPayload })
    }

    const handleMoveToTop = (id: number) => {
        const { collection, module, parent } = queryPayload
        const fromIndex = imageData.findIndex((img) => img.id === id)
        const updatedImages = handleImagesOrder(fromIndex, 0, imageData)

        setQueryData([collection, module, parent.toString()], updatedImages)
    }

    const handleScrollOnImageDrag = useCallback((e: DragEvent) => {
        const scrollSpeed = 50
        const scrollBottomTrigger = 200
        const scrollY = window.scrollY || window.pageXOffset
        const viewportHeight = window.innerHeight

        const navbar = document.querySelector('.navbar-nav')
        const navbarRect = navbar?.getBoundingClientRect()
        const navbarBottom = navbarRect?.bottom || 0

        // scroll up when mouse enters navbar
        if (e.clientY < navbarBottom) {
            window.scrollTo({ top: scrollY - scrollSpeed, behavior: 'smooth' })
            // scroll down when near bottom
        } else if (e.clientY > viewportHeight - scrollBottomTrigger) {
            window.scrollTo({ top: scrollY + scrollSpeed, behavior: 'smooth' })
        }
    }, [])

    // effect to scroll to top/bottom of images gallery when image reorder
    useEffect(() => {
        document.addEventListener('dragover', handleScrollOnImageDrag)

        return () => {
            document.removeEventListener('dragover', handleScrollOnImageDrag)
        }
    }, [handleScrollOnImageDrag])

    if (isFetching) {
        return <Loader />
    }

    return (
        <Row className="dmn-drag-row">
            <ReactDragListView {...dragProps}>
                {imageList.map((item, i) => {
                    return (
                        <AppPermissionsSwitcher
                            permission="AllowedPhotos"
                            photo={item}
                            module={module === 'project' ? 'projects' : 'properties'}
                            key={item.id}
                        >
                            {({ photo }: AllowedPhotosProps) =>
                                photo && (
                                    <Col xs={12} sm={6} xl={4} className="drag-node">
                                        <ImageCard
                                            src={blobUrls[i]}
                                            isLoading={imageUrlQueries[i]?.isPending}
                                            image={photo}
                                            handleEditorImage={handleEditorImage}
                                            handleSelectImage={handleSelectImage}
                                            handlePhotoSync={handlePhotoSync}
                                            handleMoveToTop={handleMoveToTop}
                                        />
                                    </Col>
                                )
                            }
                        </AppPermissionsSwitcher>
                    )
                })}
            </ReactDragListView>
        </Row>
    )
}

export default memo(ImagesGallery)
